/** BACKYARD WELDING PLANS™ — all customer-facing content and asset mapping. */

export interface PlanImage {
  src: string
  alt: string
  width: number
  height: number
}

export interface ProjectPage extends PlanImage {
  caption: string
}

export interface TechProof extends PlanImage {
  title: string
  project: string
  description: string
}

export interface Volume extends PlanImage {
  number: string
  title: string
  projects: string
  description: string
  /** Every project in the volume, as printed in the PDF index. */
  projectList: string[]
}

export interface Bonus extends PlanImage {
  number: string
  title: string
  description: string
}

export type SnapshotIcon = 'projects' | 'volumes' | 'dimensions' | 'bonus'

export interface SnapshotItem {
  label: string
  icon: SnapshotIcon
}

export interface Faq {
  question: string
  answer: string
}

/** Image host: light WebP files in the store's Impultienda resources. */
export const IMAGE_ORIGIN = 'https://aekpgsbqjzqntlbucbts.supabase.co'
const IMG = `${IMAGE_ORIGIN}/storage/v1/object/public/store-resources/741dd666-b287-481a-b0dc-d10ba348c13b/`

export const PRODUCT = {
  name: 'BACKYARD WELDING PLANS™',
  wordmarkA: 'Backyard',
  wordmarkB: 'Welding Plans',
  descriptor: 'Outdoor Metal Project Plans to Build & Sell',
} as const

export const COMMERCIAL_RIGHTS = {
  label: 'COMMERCIAL BUILD RIGHTS INCLUDED',
  sentence: 'Build for your own backyard — or build and sell the finished pieces.',
} as const

export const OFFER_ANCHOR = '#offer'
export const PREVIEW_ANCHOR = '#project-preview'

export const TICKER_ITEMS: string[] = [
  '30 BACKYARD PROJECTS',
  '6 DIGITAL VOLUMES',
  'DIMENSIONS & CUT LISTS',
  'BUILD & SELL CARDS',
  '5 FREE BONUS RESOURCES',
]

export const NAV_LINKS: Array<{ label: string; href: string }> = [
  { label: 'Grill Preview', href: '#project-preview' },
  { label: 'Inside the Plans', href: '#technical-proof' },
  { label: 'Volumes', href: '#volumes' },
  { label: 'Bonuses', href: '#bonuses' },
  { label: 'FAQ', href: '#faq' },
]

/** All plan pages are square (1:1). */
const PAGE_SIZE = { width: 1100, height: 1100 }

const COVERS = {
  v1: `${IMG}1792750283884_bwp-vol-1-3532daec.webp`,
  v2: `${IMG}1792750528150_bwp-vol-2-986c2f43.webp`,
  // Volumes 03–06: original covers supplied by the client (square, 1:1), served as light WebP.
  v3: `${IMG}1792779867567_bwp-cover-v3-1eacfdc5.webp`,
  v4: `${IMG}1792779873063_bwp-cover-v4-30a43940.webp`,
  v5: `${IMG}1792779878175_bwp-cover-v5-984bd97f.webp`,
  v6: `${IMG}1792779884068_bwp-cover-v6-18bfd15b.webp`,
}

export const HERO_MOCKUP: PlanImage = {
  src: `${IMG}1792766752119_bwp-hero-mockup-70bca040.webp`,
  alt: 'BACKYARD WELDING PLANS™ volumes on a steel workbench with a dimensioned plan on a tablet, a printed cut list and a backyard fire pit behind',
  width: 928,
  height: 1152,
}

export const SNAPSHOT: SnapshotItem[] = [
  { label: '30 Backyard Projects', icon: 'projects' },
  { label: '6 Organized Volumes', icon: 'volumes' },
  { label: 'Dimensions + Cut Lists', icon: 'dimensions' },
  { label: '5 Free Bonus Resources', icon: 'bonus' },
]

/** Project P06, Volume 02, PDF pages 05–10 — the complete project, in order. */
export const PREVIEW_PROJECT = {
  code: 'P06',
  name: 'Charcoal Grill Box on Legs',
  volume: 'Volume 02',
} as const

export const PROJECT_PAGES: ProjectPage[] = [
  {
    src: `${IMG}1792899236930_bwp-p06-01-finished-215033f6.webp`,
    caption: 'The finished grill — 30 × 18 × 36 in',
    alt: 'Volume 02: P06 Charcoal Grill Box on Legs finished build — 30 in / 762 mm wide, 18 in / 457.2 mm deep, 36 in / 914.4 mm high',
    ...PAGE_SIZE,
  },
  {
    src: `${IMG}1792899242186_bwp-p06-02-plan-d5ebf508.webp`,
    caption: '3-view dimensioned plan — inches & millimeters',
    alt: 'Volume 02: P06 front, side and top views with every dimension in inches and millimeters',
    ...PAGE_SIZE,
  },
  {
    src: `${IMG}1792899247367_bwp-p06-03-cutlist-512f5a79.webp`,
    caption: 'Exploded view & complete cut list',
    alt: 'Volume 02: P06 exploded view and complete cut list in inches and millimeters',
    ...PAGE_SIZE,
  },
  {
    src: `${IMG}1792899252386_bwp-p06-04-steps-1ff520aa.webp`,
    caption: 'Step-by-step fabrication',
    alt: 'Volume 02: P06 illustrated step-by-step fabrication sequence',
    ...PAGE_SIZE,
  },
  {
    src: `${IMG}1792899257332_bwp-p06-05-welds-b22b6531.webp`,
    caption: 'Weld map & fabrication details',
    alt: 'Volume 02: P06 weld map with seam locations, tack order and finish checklist',
    ...PAGE_SIZE,
  },
  {
    src: `${IMG}1792899262443_bwp-p06-06-sell-card-3ff47bec.webp`,
    caption: 'Build & Sell card — materials & maker notes',
    alt: 'Volume 02: P06 Build & Sell card with finished size, materials list and maker notes',
    ...PAGE_SIZE,
  },
]

export const PLAN_BENEFITS: string[] = ['Cut the steel.', 'Weld it square.', 'Fire it up — or sell it.']

export const TECH_PROOF: TechProof[] = [
  {
    src: `${IMG}1792899267399_bwp-tech-p07-plan-5d692d22.webp`,
    title: 'KNOW THE DIMENSIONS',
    project: 'Project 07 · Griddle Cart · Volume 02',
    description: 'Every view dimensioned before you cut a single part.',
    alt: 'Volume 02: P07 Griddle Cart three-view dimensioned plan',
    ...PAGE_SIZE,
  },
  {
    src: `${IMG}1792899272620_bwp-tech-p11-cutlist-4cc49210.webp`,
    title: 'KNOW WHAT TO CUT',
    project: 'Project 11 · Outdoor Firewood Rack · Volume 03',
    description: 'Parts, quantities, stock and exact cut sizes in one table.',
    alt: 'Volume 03: P11 Outdoor Firewood Rack exploded view and complete cut list',
    ...PAGE_SIZE,
  },
  {
    src: `${IMG}1792899277653_bwp-tech-p26-welds-9d32d0fc.webp`,
    title: 'KNOW WHERE TO WELD',
    project: 'Project 26 · Garden Walk Gate · Volume 06',
    description: 'Weld map, tack order and fit-up details for every joint.',
    alt: 'Volume 06: P26 Garden Walk Gate weld map and fabrication details',
    ...PAGE_SIZE,
  },
  {
    src: `${IMG}1792899282627_bwp-tech-p18-steps-cade4dfb.webp`,
    title: 'FOLLOW THE BUILD',
    project: 'Project 18 · Garden Arch · Volume 04',
    description: 'An illustrated sequence from cut parts to the finished piece.',
    alt: 'Volume 04: P18 Garden Arch step-by-step fabrication',
    ...PAGE_SIZE,
  },
]

const COVER_SIZE = { width: 1100, height: 1100 }

export const VOLUMES: Volume[] = [
  {
    number: 'VOLUME 01',
    title: 'Fire Pits & Fire Features',
    projects: '5 projects',
    description: 'Fire pits, grates and fireside gear — the best-selling backyard builds.',
    projectList: [
      'P01 — Square Plate Fire Pit',
      'P02 — Hexagon Fire Pit',
      'P03 — Fire Pit Log Grate',
      'P04 — Swing-Arm Campfire Cooking Grate',
      'P05 — Fireside Tool Stand',
    ],
    src: COVERS.v1,
    alt: 'Volume 01 cover: Fire Pits & Fire Features',
    ...COVER_SIZE,
  },
  {
    number: 'VOLUME 02',
    title: 'Outdoor Cooking Stations',
    projects: '5 projects',
    description: 'The charcoal grill from the preview, a griddle cart, prep tables and more for outdoor kitchens.',
    projectList: [
      'P06 — Charcoal Grill Box on Legs',
      'P07 — Griddle Cart',
      'P08 — Dutch Oven Cooking Table',
      'P09 — Outdoor Grill Prep Table',
      'P10 — Grill Tool & Towel Rack',
    ],
    src: COVERS.v2,
    alt: 'Volume 02 cover: Outdoor Cooking Stations',
    ...COVER_SIZE,
  },
  {
    number: 'VOLUME 03',
    title: 'Firewood Storage & Handling',
    projects: '5 projects',
    description: 'Log racks, a kindling-shelf rack, a firewood cart and wall brackets.',
    projectList: [
      'P11 — Outdoor Firewood Rack',
      'P12 — Firewood Rack with Kindling Shelf',
      'P13 — Porch Log Holder',
      'P14 — Firewood Cart',
      'P15 — Wall-Mounted Firewood Brackets',
    ],
    src: COVERS.v3,
    alt: 'Volume 03 cover: Firewood Storage & Handling',
    ...COVER_SIZE,
  },
  {
    number: 'VOLUME 04',
    title: 'Garden & Planter Projects',
    projects: '5 projects',
    description: 'Raised beds, trellises, a garden arch and plant stands.',
    projectList: [
      'P16 — Steel Raised Garden Bed',
      'P17 — Garden Trellis Panel',
      'P18 — Garden Arch',
      'P19 — Hose Reel Stand',
      'P20 — Three-Tier Plant Stand',
    ],
    src: COVERS.v4,
    alt: 'Volume 04 cover: Garden & Planter Projects',
    ...COVER_SIZE,
  },
  {
    number: 'VOLUME 05',
    title: 'Yard & Patio Accessories',
    projects: '5 projects',
    description: 'Quick, low-cost builds that sell in batches.',
    projectList: [
      'P21 — Boot Scraper & Brush Stand',
      'P22 — Five-Bike Rack',
      'P23 — Mailbox Post',
      'P24 — Outdoor Hook Rail',
      'P25 — Bird Feeder Pole with Two Arms',
    ],
    src: COVERS.v5,
    alt: 'Volume 05 cover: Yard & Patio Accessories',
    ...COVER_SIZE,
  },
  {
    number: 'VOLUME 06',
    title: 'Gates, Screens & Fence Accents',
    projects: '5 projects',
    description: 'Garden gates, privacy screens, a trash can corral and fence panels.',
    projectList: [
      'P26 — Garden Walk Gate',
      'P27 — Privacy Screen Panel',
      'P28 — Freestanding Privacy Screen',
      'P29 — Trash Can Corral',
      'P30 — Fence Panel with Stakes',
    ],
    src: COVERS.v6,
    alt: 'Volume 06 cover: Gates, Screens & Fence Accents',
    ...COVER_SIZE,
  },
]

export const BONUSES: Bonus[] = [
  {
    number: '01',
    title: 'Steel Purchase & Cut List Planner',
    description: 'Buy the right steel once, cut it in order and label every part.',
    src: `${IMG}1792779889450_bwp-bonus-mock-01-2432b609.webp`,
    alt: 'Bonus 01: Steel Purchase & Cut List Planner',
    ...COVER_SIZE,
  },
  {
    number: '02',
    title: 'Weekend Batch-Build Planner',
    description: 'Build three, five or ten of the same project in one weekend.',
    src: `${IMG}1792779895019_bwp-bonus-mock-02-a59183c5.webp`,
    alt: 'Bonus 02: Weekend Batch-Build Planner',
    ...COVER_SIZE,
  },
  {
    number: '03',
    title: 'Outdoor Finishing & Protection Guide',
    description: 'From raw steel to a finish that looks good in the yard and in the photo.',
    src: `${IMG}1792779900307_bwp-bonus-mock-03-51986669.webp`,
    alt: 'Bonus 03: Outdoor Finishing & Protection Guide',
    ...COVER_SIZE,
  },
  {
    number: '04',
    title: 'Local Selling Kit',
    description: 'Listing templates, photo setups and simple quotes for selling your builds.',
    src: `${IMG}1792779906319_bwp-bonus-mock-04-53e72d9b.webp`,
    alt: 'Bonus 04: Local Selling Kit — Listings, Photos & Quotes',
    ...COVER_SIZE,
  },
  {
    number: '05',
    title: 'Project Costing & Pricing Workbook',
    description: 'Know what each build really costs before you set a price.',
    src: `${IMG}1792779917536_bwp-bonus-mock-05-caf801c9.webp`,
    alt: 'Bonus 05: Project Costing & Pricing Workbook',
    ...COVER_SIZE,
  },
]

export const OFFER_FEATURES: string[] = [
  'The full Charcoal Grill Box plan — plus 29 more backyard builds',
  '3-view dimensioned plans — every measurement in inches and millimeters',
  'Complete cut lists, exploded views and step-by-step fabrication',
  'Weld maps and a Build & Sell card for every project',
]

export const FAQS: Faq[] = [
  {
    question: 'What am I buying?',
    answer:
      'A digital collection of outdoor metal project plans: 30 projects across six PDF volumes — including the Charcoal Grill Box on Legs shown above — plus five bonus resources.',
  },
  {
    question: 'Who is this collection for?',
    answer:
      'Hobby and professional welders who already know how to weld and want ready-to-build backyard projects — for their own home or to sell locally.',
  },
  {
    question: 'Is this a beginner welding course?',
    answer:
      'No. The collection gives you the plans: dimensions, cut lists, drawings, build steps and weld maps. It does not teach welding from the beginning.',
  },
  {
    question: 'What kinds of projects are included?',
    answer:
      'Charcoal grills and outdoor cooking stations, fire pits and fire features, firewood racks and carts, garden and planter projects, yard accessories, and gates, privacy screens and fence panels.',
  },
  {
    question: 'Are the plans in inches or millimeters?',
    answer: 'Both. Every dimension on every project page is printed in inches and millimeters.',
  },
  {
    question: 'Are these physical books or equipment?',
    answer:
      'The product is digital. You receive PDF files by email right after payment; steel, tools and equipment are not included.',
  },
  {
    question: 'Can I preview the plans?',
    answer:
      'Yes. All six pages of Project P06 — Charcoal Grill Box on Legs, from Volume 02, are in the preview section above, plus real pages from four more projects.',
  },
  {
    question: 'Can I sell the projects I build from these plans?',
    answer:
      'Yes. Your purchase includes permission to build projects from these plans for your own use or to sell the physical pieces you make. You may print working copies for your own use. You may not share, resell, redistribute or sublicense the digital plans, PDFs, images or bonus files.',
  },
]

export const SUPPORT_EMAIL = 'soporte@creativodigital.shop'

/** Single discreet guarantee line, shown only under the final purchase button. */
export const GUARANTEE_LINE = `7-day guarantee: if you have any problem with your purchase, email ${SUPPORT_EMAIL}.`
